import { For, Show, createSignal } from "solid-js";

import { SelectionActionBar } from "~/components/SelectionActionBar";
import { TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import {
  useArchiveAccount,
  useUpdateAccount,
} from "~/lib/hooks";
import type { AccountListItem } from "~/lib/types";

type Props = {
  wsKey: () => string;
  count: () => number;
  rows: () => AccountListItem[];
  onClear: () => void;
};

type Mode = "archive" | "tags" | "owner";

type RowOutcome = { id: string; name: string; ok: boolean; message?: string };

export function AccountsBulkBar(props: Props) {
  const archiveMutation = useArchiveAccount(props.wsKey);
  const updateMutation = useUpdateAccount(props.wsKey);

  const [mode, setMode] = createSignal<Mode | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [tagsDraft, setTagsDraft] = createSignal<string[]>([]);
  const [ownerDraft, setOwnerDraft] = createSignal<string | null>(null);
  const [ownerNameDraft, setOwnerNameDraft] = createSignal<string | null>(null);
  const [outcome, setOutcome] = createSignal<RowOutcome[] | null>(null);

  const reset = () => {
    setMode(null);
    setTagsDraft([]);
    setOwnerDraft(null);
    setOwnerNameDraft(null);
    setOutcome(null);
  };

  const close = () => {
    reset();
  };

  const dedupeTags = (a: string[], b: string[]): string[] => {
    const seen = new Map<string, string>();
    for (const t of [...a, ...b]) {
      const k = t.trim().toLowerCase();
      if (k && !seen.has(k)) seen.set(k, t.trim());
    }
    return Array.from(seen.values());
  };

  const runArchive = async () => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        await archiveMutation.mutateAsync({ accId: r.id, version: r.version });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id,
          name: r.name,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runAddTags = async () => {
    if (tagsDraft().length === 0) return;
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        const merged = dedupeTags(r.tags ?? [], tagsDraft());
        await updateMutation.mutateAsync({
          accId: r.id,
          version: r.version,
          body: { tags: merged },
        });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id,
          name: r.name,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runChangeOwner = async () => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        await updateMutation.mutateAsync({
          accId: r.id,
          version: r.version,
          body: { owner_user_id: ownerDraft() ?? null },
        });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id,
          name: r.name,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const okCount = () => outcome()?.filter((r) => r.ok).length ?? 0;
  const errCount = () => outcome()?.filter((r) => !r.ok).length ?? 0;

  return (
    <>
      <SelectionActionBar count={props.count} onClear={props.onClear}>
        <button
          type="button"
          class="btn btn-sm btn-warning"
          onClick={() => setMode("archive")}
        >
          Archive
        </button>
        <button
          type="button"
          class="btn btn-sm"
          onClick={() => setMode("tags")}
        >
          Add tags
        </button>
        <button
          type="button"
          class="btn btn-sm"
          onClick={() => setMode("owner")}
        >
          Change owner
        </button>
      </SelectionActionBar>

      <Show when={mode()}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="bulk-title"
        >
          <div class="modal-box max-w-xl">
            <h3 id="bulk-title" class="font-bold text-lg mb-3">
              <Show when={mode() === "archive"}>
                Archive {props.count()} account{props.count() === 1 ? "" : "s"}?
              </Show>
              <Show when={mode() === "tags"}>Add tags to {props.count()} accounts</Show>
              <Show when={mode() === "owner"}>Change owner on {props.count()} accounts</Show>
            </h3>

            <Show when={!outcome()}>
              <Show when={mode() === "archive"}>
                <p class="text-sm opacity-80 mb-4">
                  Archived accounts are hidden from lists. This can be undone
                  per-account from each detail page.
                </p>
              </Show>

              <Show when={mode() === "tags"}>
                <p class="text-sm opacity-80 mb-3">
                  Tags will be added to the existing set on each account (no
                  duplicates).
                </p>
                <TagsEditor
                  wsKey={props.wsKey}
                  entityType="account"
                  value={tagsDraft}
                  onChange={setTagsDraft}
                />
              </Show>

              <Show when={mode() === "owner"}>
                <p class="text-sm opacity-80 mb-3">
                  Pick a new owner (or clear the chip to unassign).
                </p>
                <UserPicker
                  wsKey={props.wsKey}
                  value={ownerDraft}
                  selectedName={ownerNameDraft}
                  onChange={(id, name) => {
                    setOwnerDraft(id);
                    setOwnerNameDraft(name);
                  }}
                />
              </Show>
            </Show>

            <Show when={outcome()}>
              {(rows) => (
                <div class="mb-4">
                  <div class="stats stats-horizontal w-full text-sm shadow-sm">
                    <div class="stat py-2">
                      <div class="stat-title">Total</div>
                      <div class="stat-value text-lg">{rows().length}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Succeeded</div>
                      <div class="stat-value text-lg text-success">{okCount()}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Failed</div>
                      <div class="stat-value text-lg text-error">{errCount()}</div>
                    </div>
                  </div>
                  <Show when={errCount() > 0}>
                    <div
                      class="mt-3 max-h-64 overflow-y-auto bg-base-200 rounded-lg p-2"
                      aria-label="Failed rows"
                    >
                      <ul class="text-xs space-y-1">
                        <For each={rows().filter((r) => !r.ok)}>
                          {(r) => (
                            <li>
                              <strong>{r.name}</strong>: {r.message ?? "Failed"}
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div class="modal-action">
              <button
                type="button"
                class="btn btn-ghost"
                onClick={close}
                disabled={busy()}
              >
                {outcome() ? "Close" : "Cancel"}
              </button>
              <Show when={!outcome()}>
                <Show when={mode() === "archive"}>
                  <button
                    type="button"
                    class="btn btn-warning"
                    onClick={runArchive}
                    disabled={busy()}
                  >
                    {busy() ? "Archiving…" : "Archive"}
                  </button>
                </Show>
                <Show when={mode() === "tags"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={runAddTags}
                    disabled={busy() || tagsDraft().length === 0}
                  >
                    {busy() ? "Tagging…" : "Add tags"}
                  </button>
                </Show>
                <Show when={mode() === "owner"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={runChangeOwner}
                    disabled={busy()}
                  >
                    {busy() ? "Updating…" : "Apply"}
                  </button>
                </Show>
              </Show>
            </div>
          </div>
        </dialog>
      </Show>
    </>
  );
}
